import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, Users, Mail, ChevronDown, ChevronUp, Search, BarChart3, Inbox, KeyRound, LogOut, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [contacts, setContacts] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwStatus, setPwStatus] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (res.ok) {
        setIsAuthenticated(true);
        fetchData();
      } else {
        setLoginError('Invalid email or password');
      }
    } catch {
      setLoginError('Connection error');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const params = `email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`;
    try {
      const [contactsRes, volunteersRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/contacts?${params}`),
        fetch(`${API_URL}/api/admin/volunteers?${params}`),
        fetch(`${API_URL}/api/admin/dashboard-stats?${params}`),
      ]);
      if (contactsRes.ok) setContacts((await contactsRes.json()).contacts);
      if (volunteersRes.ok) setVolunteers((await volunteersRes.json()).volunteers);
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (err) {
      console.error('Fetch error:', err);
    }
    setLoading(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwStatus(null);
    if (pwForm.newPw !== pwForm.confirm) {
      setPwStatus({ type: 'error', msg: 'New passwords do not match' });
      return;
    }
    if (pwForm.newPw.length < 6) {
      setPwStatus({ type: 'error', msg: 'Password must be at least 6 characters' });
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, current_password: pwForm.current, new_password: pwForm.newPw }),
      });
      if (res.ok) {
        setPassword(pwForm.newPw);
        setPwStatus({ type: 'success', msg: 'Password changed successfully' });
        setPwForm({ current: '', newPw: '', confirm: '' });
        setTimeout(() => setShowChangePassword(false), 1500);
      } else {
        const data = await res.json();
        setPwStatus({ type: 'error', msg: data.detail || 'Failed to change password' });
      }
    } catch {
      setPwStatus({ type: 'error', msg: 'Connection error' });
    }
  };

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [isAuthenticated]);

  const filteredContacts = contacts.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.subject?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredVolunteers = volunteers.filter(v =>
    v.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.interest?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 pt-24 flex items-center justify-center" data-testid="admin-login-page">
        <motion.div className="w-full max-w-md mx-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#1E3A8A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-[#1E3A8A]" />
              </div>
              <h1 className="text-2xl font-semibold text-slate-800">Admin Dashboard</h1>
              <p className="text-slate-500 mt-2">Sign in to access submissions</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Admin email"
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent outline-none"
                data-testid="admin-email-input"
                required
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent outline-none"
                data-testid="admin-password-input"
                required
              />
              {loginError && <p className="text-red-500 text-sm" data-testid="admin-login-error">{loginError}</p>}
              <Button type="submit" className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white py-3 rounded-xl" data-testid="admin-login-btn">
                Sign In
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-24" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800">Dashboard</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-slate-500 hidden md:inline">{email}</span>
            <Button onClick={() => setShowChangePassword(true)} variant="outline" className="rounded-xl gap-2 text-sm" data-testid="change-password-btn">
              <KeyRound className="w-4 h-4" /> <span className="hidden sm:inline">Change Password</span>
            </Button>
            <Button onClick={fetchData} variant="outline" className="rounded-xl text-sm" data-testid="admin-refresh-btn">
              Refresh
            </Button>
            <Button onClick={() => { setIsAuthenticated(false); setPassword(''); setEmail(''); }} variant="outline" className="rounded-xl gap-2 text-sm" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Change Password Modal */}
        {showChangePassword && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="change-password-modal">
            <motion.div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
              <h2 className="text-xl font-semibold text-slate-800 mb-6">Change Password</h2>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Current Password</label>
                  <input type="password" required value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent outline-none"
                    data-testid="current-password-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                  <input type="password" required value={pwForm.newPw} onChange={(e) => setPwForm({ ...pwForm, newPw: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent outline-none"
                    data-testid="new-password-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Confirm New Password</label>
                  <input type="password" required value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent outline-none"
                    data-testid="confirm-password-input" />
                </div>
                {pwStatus && (
                  <div className={`flex items-center gap-2 text-sm ${pwStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`} data-testid="password-change-status">
                    {pwStatus.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    {pwStatus.msg}
                  </div>
                )}
                <div className="flex gap-3 pt-2">
                  <Button type="submit" className="flex-1 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white py-3 rounded-xl" data-testid="save-password-btn">
                    Save Password
                  </Button>
                  <Button type="button" variant="outline" className="rounded-xl" onClick={() => { setShowChangePassword(false); setPwStatus(null); setPwForm({ current: '', newPw: '', confirm: '' }); }}>
                    Cancel
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Total Contacts', value: stats.total_contacts, icon: Mail, color: 'bg-blue-50 text-blue-600' },
              { label: 'New Contacts', value: stats.new_contacts, icon: Inbox, color: 'bg-amber-50 text-amber-600' },
              { label: 'Total Volunteers', value: stats.total_volunteers, icon: Users, color: 'bg-green-50 text-green-600' },
              { label: 'Village of Hope', value: stats.village_of_hope_signups, icon: BarChart3, color: 'bg-purple-50 text-purple-600' },
            ].map((stat) => (
              <div key={stat.label} className="bg-white rounded-xl p-6 border border-slate-200" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g,'-')}`}>
                <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center mb-3`}>
                  <stat.icon className="w-5 h-5" />
                </div>
                <p className="text-2xl font-bold text-slate-800">{stat.value}</p>
                <p className="text-sm text-slate-500">{stat.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'overview', label: 'All' },
            { id: 'contacts', label: 'Contacts' },
            { id: 'volunteers', label: 'Volunteers' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                activeTab === tab.id ? 'bg-[#1E3A8A] text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-[#1E3A8A]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Search by name, email, or subject..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent outline-none"
            data-testid="admin-search" />
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading...</div>
        ) : (
          <>
            {/* Contacts Table */}
            {(activeTab === 'overview' || activeTab === 'contacts') && (
              <div className="bg-white rounded-xl border border-slate-200 mb-8 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#1E3A8A]" />
                  <h2 className="font-semibold text-slate-800">Contact Submissions ({filteredContacts.length})</h2>
                </div>
                {filteredContacts.length === 0 ? (
                  <div className="px-6 py-12 text-center text-slate-400">No contact submissions yet</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {filteredContacts.map((contact, idx) => (
                      <div key={contact.id || idx} className="hover:bg-slate-50 transition-colors">
                        <button className="w-full px-6 py-4 flex items-center justify-between text-left"
                          onClick={() => setExpandedRow(expandedRow === `c-${idx}` ? null : `c-${idx}`)}
                          data-testid={`contact-row-${idx}`}>
                          <div className="flex items-center gap-4 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                              <span className="text-sm font-semibold text-[#1E3A8A]">{contact.name?.[0]?.toUpperCase()}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-800 truncate">{contact.name}</p>
                              <p className="text-sm text-slate-500 truncate">{contact.subject}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 flex-shrink-0">
                            <span className={`px-2 py-1 text-xs rounded-full ${contact.status === 'new' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{contact.status}</span>
                            <span className="text-xs text-slate-400">{new Date(contact.created_at).toLocaleDateString()}</span>
                            {expandedRow === `c-${idx}` ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                          </div>
                        </button>
                        {expandedRow === `c-${idx}` && (
                          <div className="px-6 pb-4 pl-20">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-3">
                              <div><span className="text-slate-400">Email: </span><a href={`mailto:${contact.email}`} className="text-[#1E3A8A]">{contact.email}</a></div>
                              <div><span className="text-slate-400">Phone: </span>{contact.phone || 'N/A'}</div>
                            </div>
                            <div className="bg-slate-50 p-4 rounded-lg text-sm text-slate-700">{contact.message}</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Volunteers Table */}
            {(activeTab === 'overview' || activeTab === 'volunteers') && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#1E3A8A]" />
                  <h2 className="font-semibold text-slate-800">Volunteer/Partner Applications ({filteredVolunteers.length})</h2>
                </div>
                {filteredVolunteers.length === 0 ? (
                  <div className="px-6 py-12 text-center text-slate-400">No volunteer applications yet</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {filteredVolunteers.map((vol, idx) => (
                      <div key={vol.id || idx} className="hover:bg-slate-50 transition-colors">
                        <button className="w-full px-6 py-4 flex items-center justify-between text-left"
                          onClick={() => setExpandedRow(expandedRow === `v-${idx}` ? null : `v-${idx}`)}
                          data-testid={`volunteer-row-${idx}`}>
                          <div className="flex items-center gap-4 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                              <span className="text-sm font-semibold text-green-700">{vol.name?.[0]?.toUpperCase()}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-800 truncate">{vol.name}</p>
                              <p className="text-sm text-slate-500 truncate">{vol.interest || vol.project || 'Volunteer'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 flex-shrink-0">
                            {vol.project === 'village_of_hope' && (
                              <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">Village of Hope</span>
                            )}
                            <span className={`px-2 py-1 text-xs rounded-full ${vol.status === 'new' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{vol.status}</span>
                            <span className="text-xs text-slate-400">{new Date(vol.created_at).toLocaleDateString()}</span>
                            {expandedRow === `v-${idx}` ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                          </div>
                        </button>
                        {expandedRow === `v-${idx}` && (
                          <div className="px-6 pb-4 pl-20">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-3">
                              <div><span className="text-slate-400">Email: </span><a href={`mailto:${vol.email}`} className="text-[#1E3A8A]">{vol.email}</a></div>
                              <div><span className="text-slate-400">Phone: </span>{vol.phone}</div>
                              {vol.organization && <div><span className="text-slate-400">Organization: </span>{vol.organization}</div>}
                              {vol.skills && <div><span className="text-slate-400">Skills: </span>{vol.skills}</div>}
                              {vol.availability && <div><span className="text-slate-400">Availability: </span>{vol.availability}</div>}
                            </div>
                            <div className="bg-slate-50 p-4 rounded-lg text-sm text-slate-700">{vol.message}</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
